/**
 * Phase 0 custody freeze — shown on Originator surfaces once a package is
 * `custody_status === 'ACCEPTED'`. Explains why write actions are hidden and
 * where the Originator goes next (custody requests), without fabricating any
 * holder / authority data the API has not returned.
 */
export function isInCustody(record) {
  if (!record) return false;
  return record.custody_status === 'ACCEPTED' || record.custody_state === 'ACCEPTED';
}

/**
 * True once the Originator has certified and the package is with the Provider
 * or Custodian (backend `originator_edit_lock === 'PACKAGE_CERTIFIED'`).
 * Digitize values are locked until the Provider returns the package.
 */
export function isLockedByCertification(record) {
  if (!record) return false;
  if (record.originator_edit_lock === 'PACKAGE_CERTIFIED') return true;
  return false;
}

export function CertifiedLockNotice({ className = '', children }) {
  return (
    <div
      role="status"
      aria-label="Certified and handed over"
      className={`rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 ${className}`}
    >
      <p className="font-semibold">Certified — with the Provider / Custodian</p>
      <p className="mt-1 text-[13px]">
        This batch has been certified and handed over for review. Files, document types,
        extracted values, verification and Digitize processes are locked so the certified
        snapshot cannot change; values stay visible read-only. If the Provider returns the
        batch, editing reopens here.
      </p>
      {children ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}

export function InCustodyNotice({ className = '', children }) {
  return (
    <div
      role="status"
      aria-label="In bank custody"
      className={`rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 ${className}`}
    >
      <p className="font-semibold">In bank custody</p>
      <p className="mt-1 text-[13px]">
        The custodian holds the authoritative record for this package. Files, classification,
        extraction, loan tape and admission facts are frozen for the Originator and no Digitize
        process can be re-run; values stay visible read-only. Changes now go through a custody
        request (transfer, pledge, release or copy).
      </p>
      {children ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}
